import { useState, useEffect } from 'react';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';


const ScrollToTop = ({ scrollRef }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [scrollProgress, setScrollProgress] = useState(0);
    const theme = useTheme();

    useEffect(() => {
        const container = scrollRef?.current || window;

        const updateScroll = () => {
            let scrollTop, scrollHeight, clientHeight;

            if (container === window) {
                scrollTop = window.scrollY;
                scrollHeight = document.documentElement.scrollHeight;
                clientHeight = window.innerHeight;
            } else {
                scrollTop = container.scrollTop;
                scrollHeight = container.scrollHeight;
                clientHeight = container.clientHeight;
            }

            const progress = scrollHeight > clientHeight
                ? scrollTop / (scrollHeight - clientHeight)
                : 0;

            setScrollProgress(progress);
            setIsVisible(scrollTop > 100);
        };

        container.addEventListener('scroll', updateScroll);
        updateScroll();

        return () => container.removeEventListener('scroll', updateScroll);
    }, [scrollRef]);

    const scrollToTop = () => {
        const container = scrollRef?.current || window;
        if (container === window) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            container.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const progressPercentage = Math.min(Math.round(scrollProgress * 100), 100);    
    const radiusLg = 18;
    const circumferenceLg = 2 * Math.PI * radiusLg;

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: { xs: 16, sm: 16 },
                right: { xs: 8, sm: 8 },
                zIndex: 999,
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                transition: 'all 0.3s ease',
                pointerEvents: isVisible ? 'auto' : 'none',
            }}
        >
            <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                    aria-label="Scroll to top"
                    onClick={scrollToTop}
                    sx={{
                        position: 'relative',
                        width: { xs: 40, sm: 48 },
                        height: { xs: 40, sm: 48 },
                        backgroundColor: 'neutral.lightest',
                        backdropFilter: 'blur(4px)',
                        borderRadius: '50%',
                        '&:hover': {
                            backgroundColor: 'neutral.light',
                        },
                    }}
                >
                    <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 48 48"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transform: 'rotate(-90deg)',
                        }}
                    >
                        <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#06b6d4" />
                                <stop offset="100%" stopColor="#3b82f6" />
                            </linearGradient>
                        </defs>
                        {/* Background circle */}
                        <circle
                            cx={24}
                            cy={24}
                            r={radiusLg}
                            fill="none"
                            stroke="slate.main"
                            strokeWidth="2"
                        />
                        {/* Foreground progress circle */}
                        <circle
                            cx={24}
                            cy={24}
                            r={radiusLg}
                            fill="none"
                            stroke="url(#gradient)"
                            strokeWidth="2"
                            strokeDasharray={circumferenceLg}
                            strokeDashoffset={circumferenceLg * (1 - scrollProgress)}
                            strokeLinecap="round"
                        />
                    </svg>

                    <KeyboardArrowUpIcon
                        sx={{
                            position: 'absolute',
                            color: theme.palette.cyan?.[400] || '#22d3ee',
                            width: 20,
                            height: 20,
                        }}
                    />
                </IconButton>

                <Typography
                    variant="caption"
                    sx={{
                        mt: { xs: 0.5, sm: 1 },
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: 'backgound.default',
                        backdropFilter: 'blur(4px)',
                        color: 'text.primary',
                        fontWeight: 500,
                    }}
                >
                    {progressPercentage}%
                </Typography>
            </Box>
        </Box>
    );
}

export default ScrollToTop;